import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
} from "lucide-react"
import * as React from "react"
import { Button, ButtonProps } from "../atoms/Button"
import { cn } from "../utils"

const Pagination = ({ className, ...props }: React.ComponentProps<"nav">) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn("mx-auto flex w-full justify-center", className)}
    {...props}
  />
)
Pagination.displayName = "Pagination"

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<"ul">>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn("flex flex-row items-center gap-2", className)} {...props} />
  ),
)
PaginationContent.displayName = "PaginationContent"

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<"li">>(
  ({ className, ...props }, ref) => <li ref={ref} className={cn("", className)} {...props} />,
)
PaginationItem.displayName = "PaginationItem"

const PaginationPrevious = ({
  className,
  disabled,
  ...props
}: React.ComponentProps<typeof PaginationButton> & { disabled?: boolean }) => (
  <PaginationButton
    aria-label="Go to previous page"
    size="default"
    className={cn("w-8 h-8 border", className)}
    aria-disabled={disabled}
    disabled={disabled}
    {...props}
  >
    <ChevronLeft />
  </PaginationButton>
)
PaginationPrevious.displayName = "PaginationPrevious"

const PaginationNext = ({
  className,
  disabled,
  ...props
}: React.ComponentProps<typeof PaginationButton> & { disabled?: boolean }) => (
  <PaginationButton
    aria-label="Go to next page"
    size="default"
    className={cn("w-8 h-8 border", className)}
    aria-disabled={disabled}
    disabled={disabled}
    {...props}
  >
    <ChevronRight />
  </PaginationButton>
)
PaginationNext.displayName = "PaginationNext"

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<"span">) => (
  <span
    aria-hidden
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <MoreHorizontal className="h-2 w-2" />
    <span className="sr-only">More pages</span>
  </span>
)
PaginationEllipsis.displayName = "PaginationEllipsis"

type PaginationButtonProps = {
  isActive?: boolean
  disabled?: boolean
} & ButtonProps

const PaginationButton = ({
  isActive,
  disabled,
  size = "icon",
  ...props
}: PaginationButtonProps) => (
  <Button
    type="button"
    disabled={disabled}
    aria-current={isActive ? "page" : undefined}
    variant={isActive ? "default" : "ghost"}
    size={size}
    {...props}
  />
)
PaginationButton.displayName = "PaginationButton"

const PaginationFirst = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationButton>) => (
  <PaginationButton
    aria-label={props.title}
    size="default"
    variant="outline"
    className={cn("w-8 h-8", className)}
    {...props}
  >
    <ChevronsLeft />
  </PaginationButton>
)
PaginationFirst.displayName = "PaginationFirst"

const PaginationLast = (props: React.ComponentProps<typeof PaginationButton>) => (
  <PaginationButton
    aria-label={props.title}
    size="default"
    variant="outline"
    className="w-8 h-8"
    {...props}
  >
    <ChevronsRight />
  </PaginationButton>
)
PaginationLast.displayName = "PaginationLast"

export {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationNext,
  PaginationPrevious,
}
